import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, MapPin, Video, FileText } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { uploadMedia, MAX_PHOTOS, MAX_VIDEOS } from "@/lib/upload";
import api, { formatApiErrorDetail } from "@/lib/api";
import { countWords } from "@/components/locanest/ReadMore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CATEGORY_LIST, CATEGORY_FIELDS } from "@/lib/categories";

const AMEN = ["Parking", "Lift", "Power Backup", "24x7 Water", "Security", "Gym", "Pool", "Garden", "Wifi", "Clubhouse", "AC", "Meals", "Laundry", "Loading dock", "Kitchen setup"];

export default function AddProperty() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "home",
    rent: "", deposit: "", videos: [], agreement_doc: null,
    expected_payment_inr: "", expected_payment_purpose: "token",
    address: "", locality: "", city: "", pincode: "",
    amenities: [], photos: [],
    category_fields: {},
  });
  const [loading, setLoading] = useState(false);
  const [geo, setGeo] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const descWords = countWords(form.description);

  useEffect(() => { api.get("/cities").then(({ data }) => setCities(data)); }, []);

  useEffect(() => {
    if (!form.city && !form.pincode) { setGeo(null); return; }
    const t = setTimeout(() => {
      setGeoBusy(true);
      api.get("/geocode", { params: { address: form.address, locality: form.locality, city: form.city, pincode: form.pincode } })
        .then(({ data }) => setGeo(data)).catch(() => setGeo(null)).finally(() => setGeoBusy(false));
    }, 900);
    return () => clearTimeout(t);
  }, [form.address, form.locality, form.city, form.pincode]);

  useEffect(() => {
    if (isEdit) {
      api.get(`/properties/${id}`, { params: { full: 1 } }).then(({ data }) => setForm({
        ...data,
        rent: data.rent?.toString() || "",
        deposit: data.deposit?.toString() || "",
        videos: data.videos || [], agreement_doc: data.agreement_doc || null,
        expected_payment_inr: data.expected_payment_inr?.toString() || "",
        expected_payment_purpose: data.expected_payment_purpose || "token",
        pincode: data.pincode || "",
        category_fields: data.category_fields || {},
      }));
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCF = (k, v) => setForm((f) => ({ ...f, category_fields: { ...f.category_fields, [k]: v } }));
  const toggleAmen = (a) => set("amenities", form.amenities.includes(a) ? form.amenities.filter((x) => x !== a) : [...form.amenities, a]);

  const changeCategory = (cat) => {
    setForm((f) => ({ ...f, category: cat, category_fields: {} }));
  };

  const [uploading, setUploading] = useState(null); // {label, pct}
  const [showAgreement, setShowAgreement] = useState(false);
  useEffect(() => { if (form.agreement_doc) setShowAgreement(true); }, [form.agreement_doc]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (form.photos.length + files.length > MAX_PHOTOS) return toast.error(`Max ${MAX_PHOTOS} photos per listing`);
    const errors = [];
    const results = [];
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      if (f.size > 15 * 1024 * 1024) { errors.push(`${f.name} > 15MB`); continue; }
      try {
        setUploading({ label: `Photo ${i + 1}/${files.length}`, pct: 0 });
        const res = await uploadMedia(f, "image", (pct) => setUploading({ label: `Photo ${i + 1}/${files.length}`, pct }));
        results.push(res.url);
      } catch (err) { errors.push(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || "upload failed"}`); }
    }
    setUploading(null);
    if (errors.length) toast.error(errors.join("; "));
    if (results.length) { setForm((fm) => ({ ...fm, photos: [...fm.photos, ...results] })); toast.success(`${results.length} photo${results.length === 1 ? "" : "s"} uploaded`); }
  };

  const onUploadVideo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (form.videos.length >= MAX_VIDEOS) return toast.error(`Max ${MAX_VIDEOS} videos per listing`);
    if (f.size > 100 * 1024 * 1024) return toast.error("Video must be under 100MB");
    try {
      setUploading({ label: "Video", pct: 0 });
      const res = await uploadMedia(f, "video", (pct) => setUploading({ label: "Video", pct }));
      setForm((fm) => ({ ...fm, videos: [...fm.videos, { url: res.url, name: res.name, size: res.size, content_type: res.content_type }] }));
      toast.success("Video uploaded — it will play on your listing page");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Video upload failed"); }
    finally { setUploading(null); }
  };

  const onUploadAgreement = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Agreement file must be under 10MB");
    try {
      setUploading({ label: "Agreement", pct: 0 });
      const res = await uploadMedia(f, "doc", (pct) => setUploading({ label: "Agreement", pct }));
      set("agreement_doc", { url: res.url, name: res.name, size: res.size, content_type: res.content_type });
      toast.success("Agreement paper attached");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Agreement upload failed"); }
    finally { setUploading(null); }
  };

  const removePhoto = (i) => set("photos", form.photos.filter((_, idx) => idx !== i));

  const catFields = CATEGORY_FIELDS[form.category] || [];

  const submit = async (e) => {
    e.preventDefault();
    if (form.photos.length < 5) return toast.error("At least 5 photos required (LocaNest listing standard).");
    if (descWords > 700) return toast.error(`Description is ${descWords} words — please keep it under 700 words.`);
    if (form.pincode && !/^[1-9]\d{5}$/.test(form.pincode)) return toast.error("Enter a valid 6-digit pincode");
    // Category required-field check
    for (const f of catFields) {
      if (f.required) {
        const v = form.category_fields[f.key];
        if (v === undefined || v === null || v === "") return toast.error(`${f.label} is required`);
      }
    }
    setLoading(true);
    try {
      // Coerce numeric category fields
      const cf = { ...form.category_fields };
      for (const f of catFields) {
        if (f.type === "number" && cf[f.key] !== "" && cf[f.key] !== undefined && cf[f.key] !== null) cf[f.key] = Number(cf[f.key]);
        if (f.type === "bool" && cf[f.key] === undefined) cf[f.key] = false;
      }
      const payload = {
        ...form,
        rent: form.rent ? Number(form.rent) : null,
        deposit: form.deposit ? Number(form.deposit) : null,
        videos: form.videos, agreement_doc: form.agreement_doc,
        expected_payment_inr: form.expected_payment_inr ? Number(form.expected_payment_inr) : null,
        expected_payment_purpose: form.expected_payment_inr ? form.expected_payment_purpose : null,
        pincode: form.pincode || null,
        category_fields: cf,
      };
      delete payload.latitude; delete payload.longitude;
      if (isEdit) await api.put(`/properties/${id}`, payload);
      else await api.post("/properties", payload);
      toast.success(isEdit ? "Listing updated" : "Listing published!");
      navigate("/owner");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const isShortStay = form.category === "short_stay";

  return (
    <div className="max-w-3xl mx-auto px-5 py-10">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">{isEdit ? "Edit" : "New"} listing</div>
      <h1 className="font-display text-4xl mt-2">{isEdit ? "Update your property" : "List your rental"}</h1>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="property-form">
        <div>
          <Label>Category</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="form-category-grid">
            {CATEGORY_LIST.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => changeCategory(c.key)}
                className={`px-3 py-2.5 text-sm rounded-md border text-left ${form.category === c.key ? "bg-[#2B3A67] text-white border-[#2B3A67]" : "bg-[#FDFBF7] border-[#1A1F2C]/15 hover:border-[#C85A32]"}`}
                data-testid={`form-cat-${c.key}`}
              >{c.label}</button>
            ))}
          </div>
        </div>

        <div>
          <Label>Title</Label>
          <Input required placeholder="e.g. Spacious 2BHK, Corner shop on main road…" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="form-title" />
        </div>

        <div>
          <Label>Description <span className="text-[#5C6B7A] font-normal">(max 700 words)</span></Label>
          <Textarea required rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="form-description" />
          <div className={`text-xs mt-1 ${descWords > 700 ? "text-red-600 font-medium" : "text-[#5C6B7A]"}`} data-testid="description-word-count">
            {descWords}/700 words{descWords > 700 && ` — ${descWords - 700} over the limit`}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {!isShortStay && (
            <div>
              <Label>Rent / month (₹)</Label>
              <Input required={!isShortStay} type="number" value={form.rent} onChange={(e) => set("rent", e.target.value)} data-testid="form-rent" />
            </div>
          )}
          {!isShortStay && (
            <div>
              <Label>Deposit (₹)</Label>
              <Input type="number" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} data-testid="form-deposit" />
            </div>
          )}
        </div>

        {/* PAYMENT TO BROKER (Issue H) */}
        <div className="rounded-lg border border-[#2B3A67]/20 bg-[#2B3A67]/5 p-4">
          <div className="text-sm font-semibold text-[#2B3A67]">Payment to Broker amount (optional)</div>
          <p className="text-xs text-[#5C6B7A] mt-1">Set the token / brokerage you expect from a tenant. Tenants see a &ldquo;Payment to Broker&rdquo; button with this amount pre-filled and pay via LocaNest (UPI/card). LocaNest transfers it to your bank/UPI within 3–4 days.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-3">
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" min="1" value={form.expected_payment_inr} onChange={(e) => set("expected_payment_inr", e.target.value)} placeholder="e.g. 2000" data-testid="form-expected-payment" />
            </div>
            <div>
              <Label>This amount is for</Label>
              <Select value={form.expected_payment_purpose} onValueChange={(v) => set("expected_payment_purpose", v)}>
                <SelectTrigger data-testid="form-expected-purpose"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="token">Token / booking amount</SelectItem>
                  <SelectItem value="deposit">Security deposit</SelectItem>
                  <SelectItem value="rent">First month rent</SelectItem>
                  <SelectItem value="other">Brokerage / other agreed amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* CATEGORY-SPECIFIC FIELDS */}
        {catFields.length > 0 && (
          <div>
            <Label>Category-specific details</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2" data-testid="form-category-fields">
              {catFields.map((f) => {
                const val = form.category_fields[f.key];
                if (f.type === "select") {
                  return (
                    <div key={f.key}>
                      <Label className="text-xs">{f.label}{f.required && " *"}</Label>
                      <Select value={val || ""} onValueChange={(v) => setCF(f.key, v)}>
                        <SelectTrigger data-testid={`cf-${f.key}`}><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                        <SelectContent>
                          {f.options.map((o) => {
                            const [k, l] = Array.isArray(o) ? o : [o, o];
                            return <SelectItem key={k} value={k}>{l}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                }
                if (f.type === "bool") {
                  return (
                    <div key={f.key} className="flex items-center justify-between border rounded-md px-3 py-2 h-10">
                      <Label className="text-xs mb-0">{f.label}</Label>
                      <Switch checked={!!val} onCheckedChange={(v) => setCF(f.key, v)} data-testid={`cf-${f.key}`} />
                    </div>
                  );
                }
                if (f.type === "multi") {
                  const arr = val || [];
                  return (
                    <div key={f.key} className="col-span-2">
                      <Label className="text-xs">{f.label}</Label>
                      <div className="flex flex-wrap gap-2 mt-1" data-testid={`cf-${f.key}`}>
                        {f.options.map((o) => (
                          <button
                            key={o}
                            type="button"
                            onClick={() => setCF(f.key, arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])}
                            className={`px-3 py-1.5 text-xs rounded-md border ${arr.includes(o) ? "bg-[#2B3A67] text-white border-[#2B3A67]" : "bg-[#FDFBF7] border-[#1A1F2C]/15 hover:border-[#C85A32]"}`}
                          >{o}</button>
                        ))}
                      </div>
                    </div>
                  );
                }
                if (f.type === "textarea") {
                  return (
                    <div key={f.key} className="col-span-2">
                      <Label className="text-xs">{f.label}</Label>
                      <Textarea rows={2} value={val || ""} onChange={(e) => setCF(f.key, e.target.value)} data-testid={`cf-${f.key}`} />
                    </div>
                  );
                }
                return (
                  <div key={f.key}>
                    <Label className="text-xs">{f.label}{f.required && " *"}{f.help && <span className="text-[#5C6B7A] font-normal ml-1">— {f.help}</span>}</Label>
                    <Input type={f.type} value={val || ""} onChange={(e) => setCF(f.key, e.target.value)} data-testid={`cf-${f.key}`} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* LOCATION */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>City</Label>
            <Select value={form.city} onValueChange={(v) => set("city", v)}>
              <SelectTrigger data-testid="form-city"><SelectValue /></SelectTrigger>
              <SelectContent>{cities.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Locality</Label>
            <Input required value={form.locality} onChange={(e) => set("locality", e.target.value)} placeholder="e.g. Vaishali Nagar, Koramangala…" data-testid="form-locality" />
          </div>
          <div className="col-span-1 md:col-span-2">
            <Label>Full address</Label>
            <Input required value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="House/plot no., street, landmark" data-testid="form-address" />
          </div>
          <div>
            <Label>Pincode</Label>
            <Input inputMode="numeric" maxLength={6} value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="e.g. 302021" data-testid="form-pincode" />
          </div>
          <div className="col-span-1 md:col-span-2 rounded-md border border-[#1A1F2C]/10 bg-[#F0EBE1] p-3 flex items-start gap-2" data-testid="geo-preview">
            <MapPin size={16} className="text-[#C85A32] mt-0.5 shrink-0" />
            <div className="text-sm min-w-0">
              <div className="font-medium">Map location is detected automatically</div>
              {geoBusy ? (
                <div className="text-xs text-[#5C6B7A]" data-testid="geo-status">Detecting location…</div>
              ) : geo ? (
                <div className="text-xs text-[#5C6B7A] truncate" data-testid="geo-status">
                  📍 {geo.display_name}{geo.precision === "city" || geo.precision === "country" ? " (approximate — add pincode or a clearer address for an exact pin)" : ""}
                </div>
              ) : (
                <div className="text-xs text-[#5C6B7A]" data-testid="geo-status">Enter city, locality, address and pincode — we&rsquo;ll place the pin for you.</div>
              )}
            </div>
          </div>
        </div>

        <div>
          <Label>Amenities</Label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="form-amenities">
            {AMEN.map((a) => (
              <button type="button" key={a} onClick={() => toggleAmen(a)}
                className={`px-3 py-1.5 text-xs rounded-md border ${form.amenities.includes(a) ? "bg-[#2B3A67] text-white border-[#2B3A67]" : "bg-[#FDFBF7] border-[#1A1F2C]/15 hover:border-[#C85A32]"}`}>
                {a}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Photos (min 5, up to {MAX_PHOTOS}, 15MB each) — LocaNest requires real photos</Label>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mt-2" data-testid="form-photos">
            {form.photos.map((src, i) => (
              <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-[#1A1F2C]/10">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded-full" data-testid={`remove-photo-${i}`}><X size={12} /></button>
              </div>
            ))}
            {form.photos.length < MAX_PHOTOS && (
              <label className="aspect-square rounded-md border-2 border-dashed border-[#1A1F2C]/20 flex flex-col items-center justify-center text-xs text-[#5C6B7A] cursor-pointer hover:border-[#C85A32] hover:text-[#C85A32]" data-testid="upload-photo">
                <Upload size={20} className="mb-1" /> Upload
                <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} />
              </label>
            )}
          </div>
          <div className="text-xs text-[#5C6B7A] mt-1" data-testid="photo-count">{form.photos.length}/{MAX_PHOTOS} uploaded {form.photos.length < 5 && `— add ${5 - form.photos.length} more to publish`}</div>
          {uploading && (
            <div className="mt-2 text-xs text-[#2B3A67]" data-testid="upload-progress">
              Uploading {uploading.label}… {uploading.pct}%
              <div className="h-1.5 mt-1 rounded-full bg-[#1A1F2C]/10 overflow-hidden"><div className="h-full bg-[#C85A32] transition-[width]" style={{ width: `${uploading.pct}%` }} /></div>
            </div>
          )}
        </div>

        {/* VIDEO */}
        <div>
          <Label>Video walkthrough (optional, up to {MAX_VIDEOS}, 100MB each · MP4/MOV/WebM)</Label>
          <div className="grid sm:grid-cols-2 gap-3 mt-2" data-testid="form-videos">
            {form.videos.map((v, i) => (
              <div key={v.url} className="relative rounded-md overflow-hidden border border-[#1A1F2C]/10 bg-black">
                <video src={v.url} controls preload="metadata" className="w-full aspect-video" data-testid={`form-video-${i}`} />
                <button type="button" onClick={() => set("videos", form.videos.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded-full" data-testid={`remove-video-${i}`}><X size={12} /></button>
                <div className="px-2 py-1 text-[11px] text-white/80 truncate">{v.name} · {(v.size / (1024 * 1024)).toFixed(1)} MB</div>
              </div>
            ))}
            {form.videos.length < MAX_VIDEOS && (
              <label className="aspect-video rounded-md border-2 border-dashed border-[#1A1F2C]/20 flex flex-col items-center justify-center text-xs text-[#5C6B7A] cursor-pointer hover:border-[#C85A32] hover:text-[#C85A32]" data-testid="upload-video">
                <Video size={20} className="mb-1" /> Upload video
                <input type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v,video/3gpp" className="hidden" onChange={onUploadVideo} disabled={!!uploading} />
              </label>
            )}
          </div>
        </div>

        {/* AGREEMENT PAPER — optional now, mandatory at deal completion */}
        <div className="rounded-lg border border-[#1A1F2C]/10 bg-[#F0EBE1] p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={showAgreement} onCheckedChange={(v) => { setShowAgreement(!!v); if (!v) set("agreement_doc", null); }} className="mt-0.5" data-testid="agreement-toggle" />
            <span>
              <span className="text-sm font-medium">Do you want to add your agreement paper now? (Optional)</span>
              <span className="block text-xs text-[#5C6B7A] mt-0.5">You can publish without it. The signed agreement becomes mandatory only when you confirm/complete a deal with a tenant.</span>
            </span>
          </label>
          {showAgreement && (
            <div className="mt-3" data-testid="agreement-upload-block">
              {form.agreement_doc ? (
                <div className="flex items-center gap-3 text-sm bg-white rounded-md border border-[#1A1F2C]/10 px-3 py-2">
                  <FileText size={16} className="text-[#2B3A67]" />
                  <a href={form.agreement_doc.url} target="_blank" rel="noreferrer" className="truncate underline" data-testid="agreement-file-link">{form.agreement_doc.name}</a>
                  <button type="button" onClick={() => set("agreement_doc", null)} className="ml-auto text-xs text-red-600" data-testid="agreement-remove">Remove</button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-dashed border-[#1A1F2C]/25 bg-white cursor-pointer hover:border-[#C85A32]" data-testid="upload-agreement">
                  <Upload size={14} /> Upload agreement (PDF or photo, ≤10MB)
                  <input type="file" accept="application/pdf,image/*" className="hidden" onChange={onUploadAgreement} disabled={!!uploading} />
                </label>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button type="submit" disabled={loading || !!uploading} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md h-11 px-8" data-testid="submit-property">
            {loading ? "Saving…" : isEdit ? "Update" : "Publish listing"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/owner")} className="rounded-md h-11">Cancel</Button>
        </div>
      </form>
    </div>
  );
}
